import { useEffect, useState, type FormEvent } from 'react'
import { MapPin } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import { storefrontApi } from '../lib/api'
import { formatMoney } from '../lib/format'
import { productImageUrl } from '../lib/images'
import type { CartQuote, EfiPaymentMethod, EfiPublicSettings, EfiCardBrand, EfiInstallmentQuote } from '../types/commerce'

const pickup = {
  country: 'BR', postal_code: '13178-195', state: 'SP', city: 'Sumaré',
  address: 'Rua Generoso Alves Vieira', number: '14',
  complement: 'Sala 3', neighborhood: 'Jardim Santa Carolina'
}

export function Checkout() {
  const cart = useCart()
  const navigate = useNavigate()
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', phone: '' })
  const [method, setMethod] = useState<EfiPaymentMethod>('credit')
  const [settings, setSettings] = useState<EfiPublicSettings | null>(null)
  const [settingsError, setSettingsError] = useState('')
  const [quote, setQuote] = useState<CartQuote | null>(null)
  const [quoteLoading, setQuoteLoading] = useState(true)
  const [quoteError, setQuoteError] = useState('')
  const [cardBrand, setCardBrand] = useState<EfiCardBrand>('visa')
  const [installments, setInstallments] = useState<EfiInstallmentQuote | null>(null)
  const [installmentsLoading, setInstallmentsLoading] = useState(false)
  const [installmentsError, setInstallmentsError] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    storefrontApi.checkoutSettings().then(setSettings)
      .catch(err => setSettingsError(err instanceof Error ? err.message : 'Não foi possível verificar os pagamentos.'))
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!cart.items.length) { setQuote(null); setQuoteLoading(false); return }
    setQuote(null); setQuoteError(''); setQuoteLoading(true)
    const items = cart.items.map(item => ({ variant_id: item.variantId, quantity: item.quantity }))
    storefrontApi.quoteCheckout(items, method).then(result => {
      if (!cancelled) setQuote(result)
    }).catch(err => {
      if (!cancelled) setQuoteError(err instanceof Error ? err.message : 'Falha ao calcular o pedido.')
    }).finally(() => { if (!cancelled) setQuoteLoading(false) })
    return () => { cancelled = true }
  }, [cart.items, method])

  useEffect(() => {
    let cancelled = false
    setInstallments(null)
    setInstallmentsError('')
    if (!settings?.enabled || method !== 'credit' || !quote?.can_checkout || !cart.items.length) {
      setInstallmentsLoading(false)
      return
    }
    setInstallmentsLoading(true)
    const items = cart.items.map(item => ({ variant_id: item.variantId, quantity: item.quantity }))
    storefrontApi.efiInstallments(items, cardBrand)
      .then(result => {
        if (!cancelled && Math.round(result.base_total * 100) === Math.round(quote.total * 100)) {
          setInstallments(result)
        }
      })
      .catch(err => {
        if (!cancelled) setInstallmentsError(err instanceof Error ? err.message : 'Simulação não disponível.')
      })
      .finally(() => { if (!cancelled) setInstallmentsLoading(false) })
    return () => { cancelled = true }
  }, [cart.items, cardBrand, method, quote?.total, quote?.can_checkout, settings?.enabled])

  const set = (key: keyof typeof form, value: string) => setForm(current => ({ ...current, [key]: value }))
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!quote?.can_checkout || !settings?.enabled) return
    setLoading(true); setError('')
    try {
      const result = await storefrontApi.placeOrderCheckout({
        customer: form,
        delivery: pickup,
        items: cart.items.map(item => ({ variant_id: item.variantId, quantity: item.quantity }))
      }, method)
      try {
        sessionStorage.setItem(`gk-order-${result.order_number}`, JSON.stringify({
          id: result.id, checkout_token: result.checkout_token,
          total: result.total, payment_method: method
        }))
      } catch { /* Navegação também transporta o pedido. */ }
      cart.clear()
      navigate(`/pedido/${result.order_number}`, { state: result, replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar o pedido.')
      await cart.refreshQuote()
    } finally { setLoading(false) }
  }

  if (!cart.items.length) return <div className="empty-state empty-state--page"><h1>SUA SACOLA ESTÁ VAZIA</h1><p>Adicione produtos para finalizar.</p></div>
  if (settingsError) return <div className="page-message">{settingsError}</div>
  if (!settings || quoteLoading) return <div className="page-message">Validando preço, taxas e disponibilidade...</div>
  if (quoteError || !quote) return <div className="empty-state empty-state--page"><h1>ERRO AO VALIDAR O PEDIDO</h1><p>{quoteError}</p></div>

  return <div className="checkout-page section-shell">
    <div className="checkout-main">
      <h1>FINALIZAR PEDIDO</h1>
      <form onSubmit={submit} className="checkout-form">
        <h2>CONTATO</h2>
        <input required type="email" placeholder="Email" aria-label="Email" autoComplete="email" value={form.email} onChange={e => set('email', e.target.value)} />
        <div className="form-grid">
          <input required placeholder="Nome" aria-label="Nome" autoComplete="given-name" value={form.first_name} onChange={e => set('first_name', e.target.value)} />
          <input required placeholder="Sobrenome" aria-label="Sobrenome" autoComplete="family-name" value={form.last_name} onChange={e => set('last_name', e.target.value)} />
        </div>
        <input required placeholder="Telefone" aria-label="Telefone" autoComplete="tel" inputMode="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
        <section className="gk-pickup-info" aria-label="Retirada em loja">
          <MapPin size={21} aria-hidden="true" />
          <div><h2>RETIRADA NA LOJA · GRÁTIS</h2>
            <strong>GK Maison, Sala 3</strong>
            <p>Rua Generoso Alves Vieira, 14<br />Jardim Santa Carolina · Sumaré/SP<br />CEP 13178-195</p>
            <small>Não realizamos entrega. Você poderá retirar após a confirmação do pagamento e a liberação do pedido pela loja.</small>
          </div>
        </section>
        <h2>PAGAMENTO</h2>
        <div className="payment-method-grid">
          <label className={method === 'credit' ? 'payment-method is-selected' : 'payment-method'}>
            <input type="radio" name="paymentMethod" checked={method === 'credit'} disabled={!settings.credit_enabled} onChange={() => setMethod('credit')} />
            <strong>Cartão de crédito</strong><span>Pagamento seguro na Efí Bank</span>
          </label>
          <label className={method === 'pix' ? 'payment-method is-selected' : 'payment-method'}>
            <input type="radio" name="paymentMethod" checked={method === 'pix'} disabled={!settings.pix_enabled} onChange={() => setMethod('pix')} />
            <strong>Pix via Bolix</strong><span>{settings.pix_enabled ? 'Pix com QR Code Efí. Boleto também pode estar disponível.' : 'Aguardando habilitação do Bolix pela loja'}</span>
          </label>
          <label className="payment-method">
            <input type="radio" disabled name="paymentMethod" /><strong>Débito</strong><span>Não disponível na integração atual</span>
          </label>
        </div>
        {method === 'credit' && settings.credit_enabled && quote.can_checkout && <section className="gk-installments" aria-label="Simulação de parcelas na Efí Bank">
          <h2>PARCELAMENTO NO CARTÃO</h2>
          <p className="server-note">Consulta automática das condições da Efí para esta compra. Escolha a bandeira para simular, sem digitar dados do cartão.</p>
          <label className="gk-installments__brand">Bandeira do cartão
            <select value={cardBrand} onChange={event => setCardBrand(event.target.value as EfiCardBrand)}>
              <option value="visa">Visa</option>
              <option value="mastercard">Mastercard</option>
              <option value="elo">Elo</option>
              <option value="amex">American Express</option>
            </select>
          </label>
          {installmentsLoading && <p role="status" className="server-note">Consultando as parcelas na Efí Bank...</p>}
          {installmentsError && <p role="status" className="server-note">Não foi possível consultar as parcelas agora. O valor do pagamento será informado pela Efí antes da confirmação.</p>}
          {installments && <div className="gk-installments__rows">
            {installments.installments.map(option => <div className="gk-installments__row" key={option.installments}>
              <span><strong>{option.installments}x de {formatMoney(option.installment_amount)}</strong> <small>{option.has_interest ? `Juros ${option.interest_percent.toLocaleString('pt-BR')}%` : 'Sem juros adicionais'}</small></span>
              <span>Total estimado <strong>{formatMoney(option.total_amount)}</strong></span>
            </div>)}
          </div>}
          <small>Simulação informativa consultada na Efí, não é uma seleção definitiva de parcelas. O número de parcelas, os juros e o total finais serão escolhidos e confirmados na página segura da Efí, de acordo com seu cartão e a conta da loja.</small>
        </section>}
        {!settings.enabled && <div className="form-error">O pagamento ainda não foi ativado pela GK Maison.</div>}
        {!quote.can_checkout && <div className="form-error">Os itens, o estoque ou a forma de pagamento selecionada não permitem finalizar.</div>}
        {error && <div className="form-error" role="alert">{error}</div>}
        <button className="button button--dark button--full" disabled={loading || !quote.can_checkout || !settings.enabled}>
          {loading ? 'REGISTRANDO...' : 'CONFIRMAR PEDIDO E PAGAR COM EFÍ'}
        </button>
        <p className="server-note">Você será encaminhado para o ambiente seguro da Efí. No Pix via Bolix, poderá haver opção de boleto. O pagamento só será confirmado após a operadora informar a aprovação.</p>
      </form>
    </div>
    <aside className="checkout-summary">
      <h2>RESUMO DO PEDIDO</h2>
      {quote.items.map(item => <div className="summary-item" key={item.variant_id}>
        <div>{item.primary_image_path ? <img src={productImageUrl(item.primary_image_path)} alt={item.product_name} /> : <div className="image-placeholder">GK</div>}<span>{item.quantity}</span></div>
        <p><strong>{item.product_name}</strong><small>{item.color_name} · {item.size}</small></p>
        <strong>{formatMoney(item.line_total)}</strong>
      </div>)}
      <div className="summary-total"><span>Produtos</span><strong>{formatMoney(quote.subtotal)}</strong></div>
      <div className="summary-total"><span>Retirada na loja</span><strong>Grátis</strong></div>
      {(quote.payment_fee ?? 0) > 0 && <div className="summary-total"><span>Encargo do pagamento</span><strong>{formatMoney(quote.payment_fee ?? 0)}</strong></div>}
      <div className="summary-total"><span>Total a pagar</span><strong>{formatMoney(quote.total)}</strong></div>
      <small>Os valores das parcelas e possíveis juros são consultados automaticamente na Efí. O total efetivo das parcelas será confirmado antes de pagar na página segura da Efí.</small>
    </aside>
  </div>
}
